import React, { useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';

import {
  DEFAULT_TAG_COLORS,
  getContrastInk,
  getRandomHexColor,
  parseColorToHex,
  resolveTagColor,
} from 'utils/tagColor';

import { Mui } from 'components';

const useStyles = Mui.makeStyles(theme => ({
  randomButton: {
    alignItems: 'center',
    border: 0,
    borderRadius: theme.spacing(0.75),
    cursor: 'pointer',
    display: 'inline-flex',
    flexShrink: 0,
    height: 40,
    justifyContent: 'center',
    padding: 0,
    width: 40,
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  swatches: {
    display: 'grid',
    gap: theme.spacing(0.75),
    gridTemplateColumns: 'repeat(7, 24px)',
  },
  swatch: {
    appearance: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
    cursor: 'pointer',
    display: 'block',
    height: 24,
    padding: 0,
    width: 24,
    '&:hover': {
      transform: 'scale(1.08)',
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
  },
  selectedSwatch: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`,
  },
  row: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  tooltipWrap: {
    display: 'inline-flex',
    flexShrink: 0,
    height: 40,
    lineHeight: 0,
  },
  field: {
    flex: 1,
    minWidth: 0,
    '& .MuiOutlinedInput-root': {
      boxSizing: 'border-box',
      height: 40,
    },
  },
  helperText: {
    marginLeft: 40 + theme.spacing(1) + 14,
    marginRight: 14,
    marginTop: theme.spacing(0.5),
  },
}));

function ColorPicker({ value, onChange, disabled, error, helperText }) {
  const classes = useStyles();
  const resolvedValue = resolveTagColor(value);
  const [typing, setTyping] = useState(null);
  const draft = typing !== null ? typing : value || resolvedValue;
  const parsedDraft = parseColorToHex(draft);
  const isInvalidDraft = Boolean(draft) && !parsedDraft;
  const previewColor = parsedDraft || resolvedValue;
  const contrastInk = getContrastInk(previewColor);

  const handleCommit = nextColor => {
    const hex = resolveTagColor(nextColor);
    setTyping(null);
    onChange?.(hex);
  };

  const handleDraftChange = event => {
    setTyping(event.target.value);
  };

  const handleBlur = () => {
    if (!parsedDraft) {
      setTyping(null);
      return;
    }
    handleCommit(parsedDraft);
  };

  const showError = error || isInvalidDraft;
  const fieldHelperText =
    helperText ||
    (isInvalidDraft
      ? 'Cor inválida. A prévia usa a cor padrão até um valor válido ser informado.'
      : 'Hexadecimal (#RRGGBB) ou nome CSS, como red');

  return (
    <Mui.Box>
      <Mui.Typography variant="subtitle2" color="textSecondary" gutterBottom>
        Cor
      </Mui.Typography>
      <Mui.Box className={classes.row}>
        <Mui.Tooltip title="Gerar cor aleatória">
          <span className={classes.tooltipWrap}>
            <button
              type="button"
              className={classes.randomButton}
              disabled={disabled}
              aria-label="Gerar cor aleatória"
              onClick={() => handleCommit(getRandomHexColor())}
              style={{ backgroundColor: previewColor, color: contrastInk }}>
              <Mui.Icon>sync</Mui.Icon>
            </button>
          </span>
        </Mui.Tooltip>
        <Mui.TextField
          className={classes.field}
          fullWidth
          variant="outlined"
          size="small"
          value={draft}
          disabled={disabled}
          error={showError}
          onChange={handleDraftChange}
          onBlur={handleBlur}
          inputProps={{ maxLength: 24, spellCheck: false }}
          placeholder="#1d76db"
        />
      </Mui.Box>
      <Mui.FormHelperText className={classes.helperText} error={showError}>
        {fieldHelperText}
      </Mui.FormHelperText>
      <Mui.Box mt={2}>
        <Mui.Typography variant="caption" color="textSecondary" gutterBottom component="div">
          Cores padrão
        </Mui.Typography>
        <Mui.Box className={classes.swatches} mt={1}>
          {DEFAULT_TAG_COLORS.map(swatch => {
            const selected = resolvedValue === swatch;
            return (
              <Mui.Tooltip key={swatch} title={swatch}>
                <button
                  type="button"
                  aria-label={`Selecionar cor ${swatch}`}
                  disabled={disabled}
                  className={clsx(classes.swatch, selected && classes.selectedSwatch)}
                  style={{ backgroundColor: swatch }}
                  onClick={() => handleCommit(swatch)}
                />
              </Mui.Tooltip>
            );
          })}
        </Mui.Box>
      </Mui.Box>
    </Mui.Box>
  );
}

ColorPicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
};

export default ColorPicker;
